import { useCallback, useMemo, useRef } from 'react';
// eslint-disable-next-line no-restricted-imports
import { useLocation } from 'react-router-dom';
import { useNavigate, type NavigateOptions } from './use-navigate';

export type URLSearchParamsInit =
  | Record<string, string | string[] | number | null | undefined | false>
  | URLSearchParams;

export function createSearchParams(init: string | URLSearchParamsInit) {
  return new URLSearchParams(
    typeof init === 'string' || init instanceof URLSearchParams
      ? init
      : Object.keys(init).reduce(
          (memo, key) => {
            const value = init[key];

            return memo.concat(
              Array.isArray(value)
                ? value.map((v) => [key, v.toString()])
                : [[key, value?.toString() ?? '']],
            );
          },
          [] as [string, string][],
        ),
  );
}

/**
 * A convenient wrapper for reading and writing search parameters via the
 * URLSearchParams interface.
 */
export function useSearchParams(defaultInit?: URLSearchParamsInit) {
  const defaultSearchParamsRef = useRef(createSearchParams(defaultInit ?? ''));
  const location = useLocation();
  const searchParams = useMemo(() => {
    const params = createSearchParams(location.search);

    /* eslint-disable no-restricted-syntax */
    for (const key of defaultSearchParamsRef.current.keys()) {
      if (!params.has(key)) {
        defaultSearchParamsRef.current.getAll(key).forEach((value) => {
          params.append(key, value);
        });
      }
    }
    /* eslint-enable no-restricted-syntax */

    return params;
  }, [location.search]);

  const navigate = useNavigate();
  const setSearchParams = useCallback(
    (nextInit: URLSearchParamsInit, navigateOptions?: NavigateOptions) => {
      navigate(
        {
          search: createSearchParams(nextInit).toString(),
        },
        navigateOptions,
      );
    },
    [navigate],
  );

  return [searchParams, setSearchParams] as const;
}
